/**
 * report.service.js — unified report operations.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  appendScopeQuery,
} from './_core';

export const REPORT_TYPE_OPTIONS = [
  { template_key: 'vulnerability_assessment', name: 'Vulnerability Assessment' },
  { template_key: 'executive_report', name: 'Executive Security' },
  { template_key: 'compliance_report', name: 'Compliance Gap' },
  { template_key: 'incident_report', name: 'Incident Report' },
];

export const REPORT_EXPORT_FORMAT_OPTIONS = Object.freeze([
  { value: 'pdf', label: 'PDF Security Report' },
  { value: 'json_evidence_package', label: 'JSON Evidence Package' },
]);

function parseList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }
  if (typeof value !== 'string') return [];
  return value
    .split(/[\n,]/g)
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}

function normalizeVersionNumber(value) {
  const raw = String(value || '').trim().toLowerCase();
  const token = raw.startsWith('v') ? raw.slice(1) : raw;
  const parsed = Number.parseInt(token, 10);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

function toGeneratePayload(inputPayload = {}, options = {}) {
  const selectedTemplate = String(
    options?.templateKey || options?.reportType || inputPayload?.template || inputPayload?.template_key || 'vulnerability_assessment'
  ).trim();
  const sourceScope = String(
    options?.scope || inputPayload?.source_scope || inputPayload?.scope || 'findings'
  ).trim();
  const findingIds = parseList(options?.findingIds ?? inputPayload?.finding_ids);
  const sourceKeys = parseList(
    options?.sourceKeys
    ?? inputPayload?.source_keys
    ?? inputPayload?.source_integration_keys
  );

  let assetIds = parseList(options?.assetIds ?? inputPayload?.asset_ids);
  if (assetIds.length === 0 && Array.isArray(inputPayload?.assets)) {
    assetIds = inputPayload.assets
      .map((item) => String(item?.id || item?.name || item || '').trim())
      .filter(Boolean);
  }

  return {
    template: selectedTemplate,
    source_scope: sourceScope || 'findings',
    asset_ids: assetIds,
    finding_ids: findingIds,
    source_keys: sourceKeys,
    ai_enhancement: typeof options?.aiEnhancement === 'boolean' ? options.aiEnhancement : true,
  };
}

export async function listReports(token = null, options = {}) {
  const params = new URLSearchParams();
  if (options?.includeTerminal === true) {
    params.set('include_terminal', 'true');
  }
  if (Array.isArray(options?.statuses) && options.statuses.length > 0) {
    const normalized = options.statuses
      .map((item) => String(item || '').trim().toLowerCase())
      .filter(Boolean);
    if (normalized.length > 0) {
      params.set('status', normalized.join(','));
    }
  }
  appendScopeQuery(params, options?.scopeQuery);
  const suffix = params.toString() ? `?${params.toString()}` : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${suffix}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report list failed (${response.status})`);
    throw new Error(msg);
  }

  const payload = await parseJsonOrThrow(response, 'Report list');
  return Array.isArray(payload) ? payload : [];
}

export async function getReportDetail(reportId, token = null) {
  const safeReportId = Number(reportId);
  if (!Number.isFinite(safeReportId)) throw new Error('Valid report ID is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${safeReportId}/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report detail failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Report detail');
}

export async function getReportVersions(reportId, token = null) {
  const safeReportId = Number(reportId);
  if (!Number.isFinite(safeReportId)) throw new Error('Valid report ID is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${safeReportId}/versions/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report versions failed (${response.status})`);
    throw new Error(msg);
  }

  const payload = await parseJsonOrThrow(response, 'Report versions');
  return Array.isArray(payload) ? payload : [];
}

export async function getReportVersionEvidence(reportId, versionId, token = null) {
  const safeReportId = Number(reportId);
  const safeVersionId = Number(versionId);
  if (!Number.isFinite(safeReportId) || !Number.isFinite(safeVersionId)) {
    throw new Error('Valid report and version IDs are required.');
  }

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${safeReportId}/versions/${safeVersionId}/evidence/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report evidence failed (${response.status})`);
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  const payload = await parseJsonOrThrow(response, 'Report evidence');
  return payload && typeof payload === 'object' ? payload : {};
}

function resolveFilenameFromDisposition(dispositionHeader, fallbackName) {
  const header = String(dispositionHeader || '');
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_) {
      return utf8Match[1];
    }
  }
  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  if (asciiMatch && asciiMatch[1]) return asciiMatch[1];
  return fallbackName;
}

export async function listReportExports(reportId, token = null) {
  const safeReportId = Number(reportId);
  if (!Number.isFinite(safeReportId)) throw new Error('Valid report ID is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${safeReportId}/exports/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report exports failed (${response.status})`);
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  const payload = await parseJsonOrThrow(response, 'Report exports');
  return Array.isArray(payload) ? payload : [];
}

export async function createReportExport(reportId, versionId, exportFormat = 'pdf', token = null) {
  const safeReportId = Number(reportId);
  const safeVersionId = Number(versionId);
  const normalizedFormat = String(exportFormat || '').trim().toLowerCase();
  const supportedFormats = new Set(REPORT_EXPORT_FORMAT_OPTIONS.map((row) => row.value));
  if (!Number.isFinite(safeReportId)) throw new Error('Valid report ID is required.');
  if (!Number.isFinite(safeVersionId)) throw new Error('Valid report version ID is required.');
  if (!supportedFormats.has(normalizedFormat)) throw new Error('Unsupported export format selected.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${safeReportId}/versions/${safeVersionId}/export/`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({ format: normalizedFormat }),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report export create failed (${response.status})`);
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  const payload = await parseJsonOrThrow(response, 'Report export create');
  return payload && typeof payload === 'object' ? payload : {};
}

export async function downloadReportExport(exportId, token = null) {
  const safeExportId = Number(exportId);
  if (!Number.isFinite(safeExportId)) throw new Error('Valid export ID is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/exports/${safeExportId}/download/`),
    {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report export download failed (${response.status})`);
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  const blob = await response.blob();
  const filename = resolveFilenameFromDisposition(
    response.headers.get('content-disposition'),
    `report-export-${safeExportId}.pdf`
  );

  return {
    blob,
    filename,
    size: Number.isFinite(blob.size) ? blob.size : 0,
  };
}

// Backward-compatible alias used by existing components.
export async function getReportHistory(reportId, token = null) {
  return getReportVersions(reportId, token);
}

export async function getReportVersionDetail(reportId, versionId, token = null) {
  const detail = await getReportDetail(reportId, token);
  const versions = await getReportVersions(reportId, token);
  const safeVersionNumber = normalizeVersionNumber(versionId);
  const version = versions.find((row) => Number(row?.version_number) === safeVersionNumber) || null;
  return {
    report_id: Number(reportId),
    version_id: safeVersionNumber,
    version: safeVersionNumber,
    created_at: version?.created_at || null,
    creator: version?.creator || '',
    status: version?.status || '',
    report_payload: detail,
    evidence_snapshot: detail?.evidence_summary || {},
    snapshot_hash: version?.evidence_snapshot_hash || '',
  };
}

export async function compareReportVersions(reportId, fromVersion, toVersion, token = null) {
  const safeReportId = Number(reportId);
  const safeFromVersion = normalizeVersionNumber(fromVersion);
  const safeToVersion = normalizeVersionNumber(toVersion);
  if (!Number.isFinite(safeReportId) || !Number.isFinite(safeFromVersion) || !Number.isFinite(safeToVersion)) {
    throw new Error('Valid report and version IDs are required.');
  }

  const params = new URLSearchParams({ from: `v${safeFromVersion}`, to: `v${safeToVersion}` });
  const response = await fetchWithAuthRetry(
    toApiUrl(`/reports/${safeReportId}/compare/?${params.toString()}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report comparison failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Report comparison');
}

export async function getReportTrends(reportId, token = null) {
  const versions = await getReportVersions(reportId, token);
  return {
    security_score_trend: [],
    risk_posture_summary: versions.length > 0 ? 'Versioned' : 'No Versions',
  };
}

export async function listReportTemplates(token = null) {
  const templates = await listReportTemplatesWithAvailability(token);
  return templates.map((row) => ({
    template_key: row.template_key,
    name: row.name,
  }));
}

export async function listReportTemplatesWithAvailability(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/reports/templates/'),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report templates failed (${response.status})`);
    throw new Error(msg);
  }

  const payload = await parseJsonOrThrow(response, 'Report templates');
  const rawTemplates = Array.isArray(payload?.templates)
    ? payload.templates
    : (Array.isArray(payload) ? payload : []);

  return rawTemplates.map((row) => {
    const templateKey = String(row?.template_key || row?.key || '').trim();
    const name = String(row?.name || templateKey || 'Unnamed Template').trim();
    const explicitAvailable = typeof row?.available === 'boolean' ? row.available : null;
    const availabilityStatus = String(row?.availability_status || (explicitAvailable === false ? 'unavailable' : 'available')).toLowerCase();
    const available = explicitAvailable === null ? availabilityStatus === 'available' : explicitAvailable;
    const requiredSources = Array.isArray(row?.required_sources)
      ? row.required_sources.map((item) => String(item || '').trim()).filter(Boolean)
      : [];
    const unavailableReason = String(row?.unavailable_reason || '').trim();
    return {
      template_key: templateKey,
      name,
      required_sources: requiredSources,
      availability_status: available ? 'available' : (availabilityStatus || 'unavailable'),
      available,
      unavailable_reason: available ? '' : unavailableReason,
    };
  }).filter((row) => row.template_key);
}

export async function listReportSources(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/reports/sources/'),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Report sources failed (${response.status})`);
    throw new Error(msg);
  }

  const payload = await parseJsonOrThrow(response, 'Report sources');
  const rawSources = Array.isArray(payload?.sources)
    ? payload.sources
    : (Array.isArray(payload) ? payload : []);

  return rawSources.map((row) => {
    const status = String(row?.status || 'unavailable').trim().toLowerCase();
    const customerAvailable = Boolean(row?.customer_available);
    const connectionRequired = Boolean(row?.tenant_connection_required);
    const available = customerAvailable && (status === 'implemented' || status === 'partial');

    let unavailableReason = '';
    if (!available) {
      if (connectionRequired && !customerAvailable) {
        unavailableReason = 'Tenant integration connection required.';
      } else if (status === 'internal_only') {
        unavailableReason = 'Internal-only source and not customer-accessible.';
      } else if (status === 'unavailable') {
        unavailableReason = 'Source is unavailable for this tenant.';
      } else if (status) {
        unavailableReason = `Source status: ${status}.`;
      }
    }

    return {
      name: String(row?.name || row?.provider_key || 'Unknown Source').trim(),
      provider_key: String(row?.provider_key || '').trim(),
      status,
      customer_available: customerAvailable,
      available,
      unavailable_reason: unavailableReason,
      availability_status: available ? 'available' : 'unavailable',
      tenant_connection_required: connectionRequired,
      data_capabilities: Array.isArray(row?.data_capabilities)
        ? row.data_capabilities.map((item) => String(item || '').trim()).filter(Boolean)
        : [],
    };
  });
}

export async function generateAIReport(scanSummary, options = {}, token = null) {
  const requestBody = toGeneratePayload(scanSummary || {}, options || {});

  const response = await fetchWithAuthRetry(
    toApiUrl('/reports/generate/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(requestBody),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Customer report generation failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Customer report generate');
}

export async function downloadAIReportPdf(reportId, token = null) {
  const detail = await getReportDetail(reportId, token);
  const safeReportId = Number(reportId);
  const blob = new Blob([JSON.stringify(detail || {}, null, 2)], { type: 'application/json' });
  const filename = `report-${safeReportId}.json`;
  return { blob, filename };
}

export async function sendAIReportEmail(reportId, toEmail = '', asyncMode = false, token = null) {
  void reportId;
  void toEmail;
  void asyncMode;
  void token;
  throw new Error('Email delivery is not available in the Customer Report API layer.');
}

// Backward-compatible alias used by legacy components.
export async function getReportStatus(reportId, token = null) {
  const detail = await getReportDetail(reportId, token);
  return {
    report_id: Number(reportId),
    report_status: String(detail?.metadata?.status || 'READY').toLowerCase(),
  };
}
